import '@notino/react-styleguide/dist/styleguide.css';
import '@notino/web-libraries-product-card/dist/web-libraries-product-card.css';
import * as React from 'react';

import NodeCache from 'node-cache';

import { ModalContext, Toaster } from '@notino/react-styleguide';
import { type Logger } from '@notino/shared/logging';

import { DrawerProvider } from '@components/BottomSheet/DrawerProvider';
import { ProductCardSettings } from '@components/Universals/ProductItem/ProductCardSettings';
import { LanguageProvider } from '@containers/LanguageProvider';
import { TrackingContextProvider } from '@context/tracking/TrackingContext';
import { NUSHOP_PORTAL_ROOT_ID } from '@lib/constants';

import WebView from '../../components/WebView';

import { runExponeaFunctions } from './utils';

interface IAppProps {
  mountId?: string;
  originUrl?: string;
  productDetailSsrEnabled?: boolean;
  cache?: NodeCache;
  rendererLogger?: Logger;
}

const App = ({
  mountId,
  originUrl,
  productDetailSsrEnabled,
  cache,
  rendererLogger,
}: IAppProps) => {
  React.useEffect(() => {
    window.__NUSHOP_HYDRATED = true;
    if (mountId) {
      document.getElementById(mountId)?.setAttribute('data-hydrated', 'true');
    }
  }, [mountId]);

  React.useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.keyCode === 9) {
        document.getElementById(mountId ?? '')?.classList.add('accesibility');
      }
    };

    const handleMouseClick = () => {
      document.getElementById(mountId ?? '')?.classList.remove('accesibility');
    };
    if (mountId) {
      document.getElementById(mountId)?.classList.remove('no-js');
      document.getElementById(mountId)?.classList.add('js');

      window.addEventListener('keydown', handleKeyPress);
      window.addEventListener('mousedown', handleMouseClick);
      runExponeaFunctions();
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('mousedown', handleMouseClick);
    };
  }, [mountId]);

  /**
   * note: when there is a change made here, the same change must be made in Tunnel component
   */
  return (
    <TrackingContextProvider>
      <LanguageProvider
        originUrl={originUrl ?? ''}
        cache={cache}
        rendererLogger={rendererLogger}
      >
        <ModalContext.Provider>
          <ProductCardSettings>
            <DrawerProvider>
              <WebView
                mountId={mountId}
                originUrl={originUrl}
                productDetailSsrEnabled={productDetailSsrEnabled}
              />
            </DrawerProvider>
          </ProductCardSettings>
          <div id={NUSHOP_PORTAL_ROOT_ID} />
          <Toaster />
        </ModalContext.Provider>
      </LanguageProvider>
    </TrackingContextProvider>
  );
};

export default App;
